import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkle, Warning, Users, Lightning, Check, CaretRight, MagnifyingGlass,
} from "@phosphor-icons/react";

const TAG_META = {
  "dropout-risk":      { tone: "rose",    icon: Warning },
  "fee-default":       { tone: "amber",   icon: Warning },
  "abc-credits-ready": { tone: "indigo",  icon: Check },
  "placement-eligible":{ tone: "emerald", icon: Lightning },
};

const TONE_BG = {
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function Cohort() {
  const { tag } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [running, setRunning] = useState(null);

  useEffect(() => {
    setData(null); setSelected(new Set()); setQ("");
    api.get(`/cohorts/${tag}`)
      .then(r => setData(r.data))
      .catch(() => { toast.error("Cohort not found"); nav("/app"); });
  }, [tag, nav]);

  if (!data) return <div className="p-10 text-sm text-muted-foreground">Loading cohort…</div>;

  const meta = TAG_META[tag] || { tone: "indigo", icon: Sparkle };
  const Icon = meta.icon;
  const filtered = data.students.filter(s =>
    s.name.toLowerCase().includes(q.toLowerCase()) || s.student_id.toLowerCase().includes(q.toLowerCase())
  );

  const allChecked = filtered.length > 0 && filtered.every(s => selected.has(s.student_id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allChecked) filtered.forEach(s => next.delete(s.student_id));
    else filtered.forEach(s => next.add(s.student_id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const trigger = async (action) => {
    setRunning(action.id);
    try {
      const { data: res } = await api.post(`/cohorts/${tag}/action`, {
        action: action.id,
        student_ids: Array.from(selected),
        note: action.label,
      });
      toast.success(res.message);
      setSelected(new Set());
    } catch {
      toast.error("Action failed. Please retry.");
    } finally {
      setRunning(null);
    }
  };

  return (
    <div data-testid="cohort-page">
      <PageHeader
        overline={
          <Link to="/app" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
            <ArrowLeft size={11}/> AI predictions
          </Link>
        }
        title={data.title}
        description={data.narrative}
        action={
          <div className={`px-3 py-2 border rounded-md text-[11px] inline-flex items-center gap-1.5 ${TONE_BG[meta.tone]}`}>
            <Icon size={12} weight="bold"/>
            Predicted <b className="ml-0.5">{data.headline_count.toLocaleString()}</b> · Drilled into {data.actual_count}
          </div>
        }
      />

      <div className="p-6 lg:p-8 grid lg:grid-cols-12 gap-6">
        {/* Left: Student table */}
        <div className="lg:col-span-8 eos-card p-0 overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Users size={16} weight="duotone"/>
              <div className="font-heading font-semibold text-base">
                {selected.size > 0 ? `${selected.size} selected` : `${filtered.length} students`}
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-md bg-secondary">
              <MagnifyingGlass size={14} className="text-muted-foreground"/>
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search…"
                className="bg-transparent text-sm outline-none w-48"
                data-testid="cohort-search"
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No matching students in this cohort.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-secondary text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 w-10">
                    <input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="cohort-select-all"/>
                  </th>
                  {["Student", "Program", "Att%", "CGPA", "Suggested intervention"].map(h => (
                    <th key={h} className="text-left px-3 py-2.5 text-[11px] uppercase tracking-[0.15em] font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <tr key={s.student_id} className={`border-t border-border hover:bg-secondary/40 ${selected.has(s.student_id) ? "bg-[hsl(20_90%_48%)]/5" : ""}`} data-testid={`cohort-row-${s.student_id}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(s.student_id)}
                        onChange={() => toggleOne(s.student_id)}
                        data-testid={`cohort-check-${s.student_id}`}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-medium">{s.name}</div>
                      <div className="text-[11px] text-muted-foreground font-mono-eos">{s.student_id} · {s.apaar_id}</div>
                    </td>
                    <td className="px-3 py-3 text-muted-foreground text-xs">{s.program} · Y{s.year}</td>
                    <td className="px-3 py-3 font-mono-eos">{s.attendance}%</td>
                    <td className="px-3 py-3 font-mono-eos">{s.cgpa}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{s.intervention}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Right: AI Suggestions panel */}
        <div className="lg:col-span-4 space-y-4">
          <div className="eos-card">
            <div className="flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-[hsl(20_90%_48%)]"/>
              <div className="eos-overline">AI-recommended actions</div>
            </div>
            <div className="font-heading font-semibold text-base mt-1">
              {selected.size > 0 ? `Apply to ${selected.size} selected` : `Apply to all ${data.actual_count}`}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Claude Sonnet 4.5 ranked these by historical effectiveness for this cohort.
            </p>

            <div className="mt-4 space-y-2">
              {data.suggestions.map(a => (
                <button
                  key={a.id}
                  onClick={() => trigger(a)}
                  disabled={running === a.id}
                  className={`w-full text-left p-3.5 border rounded-md transition-colors group ${
                    a.primary
                      ? "border-[hsl(244_53%_20%)] bg-[hsl(244_53%_20%)] text-white hover:bg-[hsl(244_53%_15%)]"
                      : "border-border hover:border-foreground/40 bg-white"
                  } ${running === a.id ? "opacity-60" : ""}`}
                  data-testid={`cohort-action-${a.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-heading font-semibold text-sm tracking-tight">
                      {running === a.id ? "Running…" : a.label}
                    </div>
                    <CaretRight size={12} weight="bold" className={a.primary ? "text-[hsl(20_90%_55%)]" : "text-muted-foreground group-hover:text-foreground"}/>
                  </div>
                  <div className={`text-[11px] mt-1 ${a.primary ? "text-white/70" : "text-muted-foreground"}`}>{a.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="eos-card">
            <div className="eos-overline">Why this cohort matters</div>
            <p className="text-sm text-muted-foreground mt-2">
              {tag === "dropout-risk" && "Each retained student is worth ~₹2.4 L in fee revenue + improves NIRF retention score."}
              {tag === "fee-default" && "Early EMI conversion has 64% historical success rate. Estimated recovery ≈ ₹62 L."}
              {tag === "abc-credits-ready" && "ABC sync is mandatory under NEP 2020. Audit window closes Feb 28."}
              {tag === "placement-eligible" && "Placement % feeds directly into NIRF, NAAC and admission marketing."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
